import { useOutOfSessionsModal } from '@components'
import { useEffect, useState } from 'react'

type BannerType = 'out-of-sessions' | 'auto-replenish-failed' | null

interface UseUsagePricingParams {
  usageBasedPricingEnabled?: boolean
  freeSessionsRemaining?: number
  paidSessionsRemaining?: number
  isFirstTimePurchase?: boolean
  autoReplenishFailed?: boolean
}

interface UseUsagePricingReturn {
  // Banner state
  showBanner: boolean
  bannerType: BannerType
  isFirstTimePurchase: boolean
  dismissBanner: () => void
  resetBannerDismissed: () => void

  // Modal state
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void

  // Session counts
  freeSessionsRemaining: number
  paidSessionsRemaining: number
  totalSessionsRemaining: number

  // Flags
  isOutOfAllSessions: boolean
  isOutOfFreeSessions: boolean
  autoReplenishFailed: boolean
}

/**
 * Hook for managing usage based pricing state and UI components
 */
export const useUsageBasedPricing = ({
  usageBasedPricingEnabled = false,
  freeSessionsRemaining = 0,
  paidSessionsRemaining = 0,
  isFirstTimePurchase = false,
  autoReplenishFailed = false
}: UseUsagePricingParams): UseUsagePricingReturn => {
  const [showBanner, setShowBanner] = useState<boolean>(false)
  const [bannerType, setBannerType] = useState<BannerType>(null)
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(false)

  const { isOpen, openModal, closeModal } = useOutOfSessionsModal()

  // Determine banner state based on conditions
  useEffect(() => {
    if (!usageBasedPricingEnabled) {
      setShowBanner(false)
      return
    }

    // Priority: auto-replenish failed takes precedence
    if (autoReplenishFailed && !bannerDismissed) {
      setShowBanner(true)
      setBannerType('auto-replenish-failed')
      return
    }

    // Show out of free sessions banner
    if (
      freeSessionsRemaining === 0 &&
      paidSessionsRemaining > 0 &&
      !bannerDismissed
    ) {
      setShowBanner(true)
      setBannerType('out-of-sessions')
      return
    }

    setShowBanner(false)
  }, [
    usageBasedPricingEnabled,
    freeSessionsRemaining,
    paidSessionsRemaining,
    autoReplenishFailed,
    bannerDismissed
  ])

  // Removed automatic modal trigger - modal will only open when user tries to start a session

  const dismissBanner = (): void => {
    setBannerDismissed(true)
    setShowBanner(false)
  }

  const resetBannerDismissed = (): void => {
    setBannerDismissed(false)
  }

  return {
    // Banner state
    showBanner,
    bannerType,
    isFirstTimePurchase,
    dismissBanner,
    resetBannerDismissed,

    // Modal state
    isModalOpen: isOpen,
    openModal,
    closeModal,

    // Session counts
    freeSessionsRemaining,
    paidSessionsRemaining,
    totalSessionsRemaining: freeSessionsRemaining + paidSessionsRemaining,

    // Flags
    isOutOfAllSessions:
      freeSessionsRemaining === 0 && paidSessionsRemaining === 0,
    isOutOfFreeSessions: freeSessionsRemaining === 0,
    autoReplenishFailed
  }
}
